import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import { api, INR, formatApiError } from "@/lib/api";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Lightweight override dialog shown when duplicating a single invoice. Lets
 * admins tweak invoice_number, amount, invoice_date, due_date before saving.
 *
 * Props:
 *   open, onOpenChange — dialog visibility
 *   source — the invoice being duplicated (needs invoice_number, amount)
 *   onSubmit(overrides) — called with the override payload on save
 */
export default function DuplicateInvoiceDialog({ open, onOpenChange, source, onSubmit }) {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [pdfFileId, setPdfFileId] = useState(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open || !source) return;
    setInvoiceNumber(`${source.invoice_number || "INV"}-COPY`);
    setAmount(String(source.amount || ""));
    setInvoiceDate("");
    setDueDate("");
    setPdfFileId(null);
    setPdfFilename("");
  }, [open, source]);

  const onPdfChosen = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPdfFileId(data.file_id);
      setPdfFilename(data.original_filename || file.name);
      toast.success("New PDF uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    const payload = {};
    if (invoiceNumber && invoiceNumber.trim()) payload.invoice_number = invoiceNumber.trim();
    if (amount !== "" && amount !== null) payload.amount = Number(amount);
    if (invoiceDate) payload.invoice_date = invoiceDate;
    if (dueDate) payload.due_date = dueDate;
    if (pdfFileId) payload.pdf_file_id = pdfFileId;
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-sm max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="duplicate-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">Duplicate invoice</DialogTitle>
        </DialogHeader>
        {source && (
          <div className="text-sm text-ink-2 mb-2">
            Copying <span className="font-semibold text-ink">{source.invoice_number}</span>
            {" · "}{INR(source.amount)} · {source.entity_name}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="eyebrow">
              New Invoice # <span className="text-[color:var(--overdue)]">*</span>
            </Label>
            <Input
              className="rounded-sm mt-1 tabular"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              data-testid="dup-invoice-number"
            />
          </div>
          <div>
            <Label className="eyebrow">
              Amount (₹) <span className="text-[color:var(--overdue)]">*</span>
            </Label>
            <Input
              type="number"
              className="rounded-sm mt-1 tabular"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="dup-amount"
            />
          </div>
          <div>
            <Label className="eyebrow">Invoice Date (leave blank for today)</Label>
            <DatePicker
              testId="dup-invoice-date"
              value={invoiceDate}
              onChange={(v) => setInvoiceDate(v)}
              placeholder="Defaults to today"
            />
          </div>
          <div>
            <Label className="eyebrow">Due Date (leave blank to auto-compute)</Label>
            <DatePicker
              testId="dup-due-date"
              value={dueDate}
              onChange={(v) => setDueDate(v)}
              placeholder="Invoice date + payment timeline"
            />
          </div>
          <div>
            <Label className="eyebrow">Invoice PDF</Label>
            {pdfFileId ? (
              <div
                className="mt-1 flex items-center justify-between rounded-sm border border-line bg-[color:var(--bone)] px-3 py-2 text-sm"
                data-testid="dup-pdf-attached"
              >
                <span className="truncate">{pdfFilename || "Uploaded PDF"}</span>
                <button
                  type="button"
                  onClick={() => {
                    setPdfFileId(null);
                    setPdfFilename("");
                  }}
                  className="text-ink-2 hover:text-ink ml-2"
                  aria-label="Remove uploaded PDF"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <label
                  className="mt-1 inline-flex items-center gap-2 rounded-sm border border-line bg-card px-3 py-2 text-sm cursor-pointer hover:bg-[color:var(--bone)]"
                  data-testid="dup-pdf-picker"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading…" : "Upload replacement PDF"}
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => onPdfChosen(e.target.files?.[0])}
                    data-testid="dup-pdf-input"
                  />
                </label>
                <div className="text-[10px] text-ink-2 mt-1">
                  Leave blank to copy the source invoice&apos;s PDF automatically.
                </div>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">
            Cancel
          </Button>
          <Button
            onClick={submit}
            className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
            data-testid="dup-submit"
          >
            Save Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
